import { Box, Button, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import modalStyle from "../../styles/modalStyle";
import apiClient from "../../axiosClient";
import React, { useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import DropDownValue from "../baseComponents/dropdownvalue";
import { useSelector } from "react-redux";
import User from "../../interfaces/user";
import { AppDispatch, RootState } from "../../store/store";
import RoleManagement from "../../security/roleManagement";
import { loadUsers } from "../../store/slices/userSlice";
import { decodedJWT, getUserRole } from "../../utils/commonUtils";
import { useAppDispatch } from "../../store/hooks";
import UserService from "../../services/userService";

interface HandleRemoveUsersModalProps {
    modalVisible: boolean;
    closeModal: () => void;
    reloadFunction: () => void;
};

const RemoveUserModal: React.FC<HandleRemoveUsersModalProps> = ({ modalVisible, closeModal, reloadFunction }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<string>('');
    const users: User[] = useSelector((state: RootState) => state.userList.users);

    const token = decodedJWT();
    const userRole = getUserRole(token);
    const dispatch: AppDispatch = useAppDispatch();

    const finalUsers = RoleManagement.isAdmin(userRole)
        // Admins should not be able to remove other admins
        ? users.filter(val => !RoleManagement.isAdmin(val.user_role))
        : RoleManagement.isSupervisor(userRole)
        // Supervisors should not be able to remove admins and other supervisors
        ? users.filter(val => !RoleManagement.isAdmin(val.user_role) && !RoleManagement.isSupervisor(val.user_role))
        : users;

    const handleCloseModal = () => {
        setSelectedUser('');
        closeModal();
    };

    const handleSubmit = async () => {
        if (selectedUser.length < 1) {
            alert("Please select a user.");
            return;
        }
        try {
            setLoading(true);
            let response = await apiClient.delete(`/api/v1/user/${selectedUser}`);
            setLoading(false);
            if (response.status === 200) {
                alert(response.data);
                await fetchUserList();
                reloadFunction();
                handleCloseModal();
            }
        } catch (error: any) {
            setLoading(false);
            alert(error.response.data);
            console.error(error);
        }
    };

    const fetchUserList = async () => {
        const query = RoleManagement.isSuperAdmin(userRole) ? '?user_role=Admin' : '';
        const response = await UserService.getUsers(query);
        if (response) {
            dispatch(loadUsers(response.user_responses as User[]));
        }
    };

    return (
        <Modal open={modalVisible} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                        <CircularProgress size={50} />
                    </Box>
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography id="modal-title" variant="h6">Remove {RoleManagement.isSuperAdmin(userRole) ? 'Admin' : 'User'}
                            </Typography>
                            <IconButton onClick={handleCloseModal}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <DropDownValue
                            label="Select User"
                            items={finalUsers}
                            selectedValue={selectedUser}
                            onSelection={val => setSelectedUser(val)}
                            required
                            keyProp="username"
                            valueProp="username"
                            displayProp="username"
                            style={{ mt: 1, mb: 2 }}
                        />
                        <Box sx={{ mt: 1}} textAlign="right">
                            <Button variant="contained" color="primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default RemoveUserModal;
